import {
  HIDDEN_PRODUCT_TAG,
  SHOPIFY_GRAPHQL_API_ENDPOINT,
  TAGS,
} from "../constants";
import { isShopifyError } from "../type-guards";
import { ensureStartWith } from "../utils";
import {
  addToCartMutation,
  createCartMutation,
  editCartItemsMutation,
  removeFromCartMutation,
} from "./mutations/cart";
import { getCartQuery } from "./queries/cart";
import { getCollectionProductsQuery, getCollectionsQuery } from "./queries/collection";
import { getMenuQuery } from "./queries/menu";
import { getProductQuery, getProductRecommendationsQuery, getProductsQuery } from "./queries/product";
import {
  Cart,
  Collection,
  Connection,
  Image,
  Menu,
  Page,
  Product,
  ShopifyAddToCartOperation,
  ShopifyCart,
  ShopifyCartOperation,
  ShopifyCollection,
  ShopifyCollectionProductsOperation,
  ShopifyCollectionsOperation,
  ShopifyCreateCartOperation,
  ShopifyMenuOperation,
  ShopifyPageOperation,
  ShopifyPagesOperation,
  ShopifyProduct,
  ShopifyProductOperation,
  ShopifyProductRecommendationsOperation,
  ShopifyProductsOperation,
  ShopifyRemoveFromCartOperation,
  ShopifyUpdateCartOperation,
} from "./types";
import { NextRequest, NextResponse } from "next/server";
import { getPageQuery, getPagesQuery } from "./queries/page";

const domain = process.env.SHOPIFY_STORE_DOMAIN
  ? ensureStartWith(process.env.SHOPIFY_STORE_DOMAIN, "https://")
  : "";
const endpoint = `${domain}${SHOPIFY_GRAPHQL_API_ENDPOINT}`;
const key = process.env.SHOPIFY_STOREFRONT_ACCESS_TOKEN;

type ExtractVariables<T> = T extends { variables: object } ? T["variables"] : never;

export async function shopifyFetch<T>({
  cache = "no-store",
  headers,
  query,
  tags,
  variables,
}: {
  cache?: RequestCache;
  headers?: HeadersInit;
  query: string;
  tags?: string[];
  variables?: ExtractVariables<T>;
}): Promise<{ status: number; body: T } | never> {
  try {
    const result = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": key!,
        ...headers,
      },
      body: JSON.stringify({ ...(query && { query }), ...(variables && { variables }) }),
      cache,
      ...(tags && { next: { tags } }),
    });

    const body = await result.json();

    if (body.errors) {
      throw body.errors[0];
    }

    return { status: result.status, body };
  } catch (error) {
    if (isShopifyError(error)) {
      throw {
        cause: (error as any).cause?.toString() || "unknown",
        status: (error as any).status || 500,
        message: (error as any).message,
        query,
      };
    }
    throw { error, query } as any;
  }
}

function removeEdgesAndNodes<T>(array: Connection<T>): T[] {
  return array.edges.map((edge) => edge?.node);
}

function reshapeImages(images: Connection<Image>, productTitle: string) {
  const flattened = removeEdgesAndNodes(images);
  return flattened.map((image) => {
    const filename = image.url.match(/.*\/(.*)\..*/)?.[1];
    return { ...image, altText: image.altText || `${productTitle} - ${filename}` };
  });
}

function reshapeProduct(product: ShopifyProduct, filterHiddenProducts: boolean = true) {
  if (!product || (filterHiddenProducts && product.tags.includes(HIDDEN_PRODUCT_TAG))) {
    return undefined;
  }

  const { images, variants, collections, ...rest } = product;
  return { 
    ...rest, 
    images: reshapeImages(images, product.title), 
    variants: removeEdgesAndNodes(variants),
    collections: collections ? removeEdgesAndNodes(collections) : undefined
  } as unknown as Product;
}

function reshapeProducts(products: ShopifyProduct[]) {
  const reshapedProducts: Product[] = [];
  for (const product of products) {
    if (product) {
      const reshapedProduct = reshapeProduct(product);
      if (reshapedProduct) reshapedProducts.push(reshapedProduct as Product);
    }
  }
  return reshapedProducts;
}

export async function getMenu(handle: string): Promise<Menu[]> {
  const res = await shopifyFetch<ShopifyMenuOperation>({
    query: getMenuQuery,
    tags: [TAGS.collections],
    variables: { handle },
  });
  return (
    (res as any).body?.data?.menu?.items.map((item: { title: string; url: string }) => ({
      title: item.title,
      path: item.url.replace(domain, "").replace("/collections", "/shop").replace("/pages", ""),
    })) || []
  );
}

export async function getProducts({ query, reverse, sortKey }: { query?: string; reverse?: boolean; sortKey?: string; }): Promise<Product[]> {
  const res = await shopifyFetch<ShopifyProductsOperation>({
    query: getProductsQuery,
    tags: [TAGS.products],
    cache: "no-store",
    variables: { query, reverse, sortKey },
  });
  return reshapeProducts(removeEdgesAndNodes((res as any).body.data.products));
}

function reshapeCollection(collection: ShopifyCollection): Collection | undefined {
  if (!collection) return undefined;
  return { ...collection, path: `/search/${collection.handle}` } as Collection;
}

function reshapeCollections(collections: ShopifyCollection[]) {
  const reshapedCollections: Collection[] = [];
  for (const collection of collections) {
    if (collection) {
      const reshapedCollection = reshapeCollection(collection);
      if (reshapedCollection) reshapedCollections.push(reshapedCollection);
    }
  }
  return reshapedCollections;
}

export async function getCollections(): Promise<Collection[]> {
  const res = await shopifyFetch<ShopifyCollectionsOperation>({
    query: getCollectionsQuery,
    tags: [TAGS.collections],
    cache: "no-store",
  });
  const shopifyCollections = removeEdgesAndNodes((res as any)?.body?.data?.collections);
  const reshapedCollections = reshapeCollections(shopifyCollections).filter((collection) => !collection.handle.startsWith("hidden"));
  return reshapedCollections;
}

export async function getCollectionProducts({ collection, reverse, sortKey }: { collection: string; reverse?: boolean; sortKey?: string; }): Promise<Product[]> {
  const res = await shopifyFetch<ShopifyCollectionProductsOperation>({
    query: getCollectionProductsQuery,
    tags: [TAGS.collections, TAGS.products],
    variables: { handle: collection, reverse, sortKey: sortKey === "CREATED_AT" ? "CREATED" : sortKey },
  });
  if (!(res as any).body.data.collection) return [];
  return reshapeProducts(removeEdgesAndNodes((res as any).body.data.collection.products));
}

export async function getProduct(handle: string): Promise<Product | undefined> {
  const res = await shopifyFetch<ShopifyProductOperation>({
    query: getProductQuery,
    tags: [TAGS.products],
    cache: "no-store",
    variables: { handle },
  });
  return reshapeProduct((res as any).body.data.product, false) as Product | undefined;
}

export async function getProductRecommendations(productId: string): Promise<Product[]> {
  const res = await shopifyFetch<ShopifyProductRecommendationsOperation>({
    query: getProductRecommendationsQuery,
    tags: [TAGS.products],
    variables: { productId },
  });
  return reshapeProducts((res as any).body.data.productRecommendations);
}

function reshapeCart(cart: ShopifyCart): Cart {
  if (!cart) throw new Error('Cart is null or undefined');
  if (!cart.cost?.totalTaxAmount) {
    cart.cost.totalTaxAmount = { amount: "0.0", currencyCode: "USD" } as any;
  }
  const lines = removeEdgesAndNodes(cart.lines);
  // Reshape collections in cart line items
  const reshapedLines = lines.map((line: any) => {
    if (line.merchandise?.product?.collections) {
      return {
        ...line,
        merchandise: {
          ...line.merchandise,
          product: {
            ...line.merchandise.product,
            collections: removeEdgesAndNodes(line.merchandise.product.collections)
          }
        }
      };
    }
    return line;
  });
  return { ...cart, lines: reshapedLines } as unknown as Cart;
}

export async function createCart(buyerIdentity?: { email: string; phone?: string; customerAccessToken?: string; }): Promise<Cart> {
  const res = await shopifyFetch<ShopifyCreateCartOperation>({
    query: createCartMutation,
    variables: buyerIdentity ? { input: { buyerIdentity } } : undefined,
    cache: "no-store",
  });
  return reshapeCart((res as any).body.data.cartCreate.cart);
}

export async function getCart(cartId: string | undefined): Promise<Cart | undefined> {
  if (!cartId) return undefined;
  const res = await shopifyFetch<ShopifyCartOperation>({
    query: getCartQuery,
    variables: { cartId },
    tags: [TAGS.cart],
    cache: "no-store",
  });
  if (!(res as any).body.data.cart) return undefined;
  return reshapeCart((res as any).body.data.cart);
}

export async function removeFromCart(cartId: string, lineIds: string[]): Promise<Cart> {
  const res = await shopifyFetch<ShopifyRemoveFromCartOperation>({
    query: removeFromCartMutation,
    variables: { cartId, lineIds },
    cache: "no-store",
  });
  return reshapeCart((res as any).body.data.cartLinesRemove.cart);
}

export async function updateCart(cartId: string, lines: { id: string; merchandiseId: string; quantity: number }[]): Promise<Cart> {
  const res = await shopifyFetch<ShopifyUpdateCartOperation>({
    query: editCartItemsMutation,
    variables: { cartId, lines },
    cache: "no-store",
  });
  return reshapeCart((res as any).body.data.cartLinesUpdate.cart);
}

export async function addToCart(cartId: string, lines: { merchandiseId: string; quantity: number }[]): Promise<Cart> {
  const res = await shopifyFetch<ShopifyAddToCartOperation>({
    query: addToCartMutation,
    variables: { cartId, lines },
    cache: "no-cache",
  });
  const { cart, userErrors } = (res as any).body.data.cartLinesAdd;
  if (userErrors && userErrors.length > 0) {
    throw new Error(`Cart validation errors: ${userErrors.map((e: any) => e.message).join(', ')}`);
  }
  if (!cart) {
    throw new Error(`Failed to add items to cart - cart is null.`);
  }
  return reshapeCart(cart);
}

export async function revalidate(req: NextRequest): Promise<NextResponse> {
  return NextResponse.json({ status: 200, revalidated: true, now: Date.now() });
}

export async function getPage(handle: string): Promise<Page> {
  const res = await shopifyFetch<ShopifyPageOperation>({ query: getPageQuery, cache: "no-store", variables: { handle } });
  return (res as any).body.data.pageByHandle;
}

export async function getPages(): Promise<Page[]> {
  const res = await shopifyFetch<ShopifyPagesOperation>({ query: getPagesQuery, cache: "no-store" });
  return removeEdgesAndNodes((res as any).body.data.pages);
}


